import React, { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Text from 'design-system/Text';
import { Button, Spacer } from 'design-system';
import Links from 'utils/links';
import { useAppState } from 'hooks';
import { selectOnboardingTradingAccountForOnboardingFunnel } from 'features/invest/reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { getPendingTopupsForAccountId, setSelectedTradingAccountId, updateWealthAccounts } from 'actions/invest';
import { useDepositToGIA } from 'features/invest/hooks/useDepositToGIA';
import ProgressCard from './ProgressCard';
import { AccountTypeDisplay } from '../../constants';
const strings = {
    timeInfo: '< 1 minute',
    completedTitle: 'Funds transferred',
    pendingTopUpsTitle: 'Under review',
    desc: (type) => `Easily fund your ${AccountTypeDisplay[type]} in seconds`,
    incompleteTitle: (type) => `Deposit to your investment\xa0account (${AccountTypeDisplay[type]})`,
};
const FundSecclAccountProgressCard = ({ status, accountId }) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const account = useAppSelector(selectOnboardingTradingAccountForOnboardingFunnel);
    const hasFunded = account?.hasFunded;
    const accountType = account?.account.accountType || 'GIA';
    const hasPendingTopups = useAppSelector((state) => state.invest.pendingTopUpInfoForAccountId?.[accountId]?.hasPendingTopups);
    const isActiveStep = status === 'ACTIVE' && !hasFunded;
    const completed = !!(status === 'ACTIVE' && !!hasFunded);
    const { onDeposit } = useDepositToGIA('onboarding');
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    useEffect(() => {
        if (isActiveStep && accountId) {
            dispatch(getPendingTopupsForAccountId(accountId));
        }
    }, [accountId, isActiveStep]);
    const loadData = useCallback(async () => {
        const { isFetchingWealthAccounts } = store.getState().unpersistedInvestReducer;
        if (status === 'ACTIVE' && isActiveStep && !isFetchingWealthAccounts) {
            dispatch(updateWealthAccounts());
        }
    }, [isActiveStep, status]);
    useFocusEffect(useCallback(() => {
        loadData();
    }, [loadData]));
    const appStateChangeCallbacks = useMemo(() => ({
        didBecomeActive: loadData,
    }), [loadData]);
    useAppState(appStateChangeCallbacks);
    const handleAddFunds = useCallback(() => {
        if (scrambled)
            return;
        if (accountId) {
            dispatch(setSelectedTradingAccountId(accountId));
            onDeposit();
        }
    }, [onDeposit, scrambled, accountId]);
    const handleMoreInfo = useCallback(() => {
        Links.openDepositFAQ();
    }, []);
    return (<ProgressCard timeInfo={strings.timeInfo} alwaysShowTimeline completed={completed} isActiveStep={isActiveStep} lightBg title={
        // eslint-disable-next-line no-nested-ternary
        completed
            ? strings.completedTitle
            : hasPendingTopups
                ? strings.pendingTopUpsTitle
                : strings.incompleteTitle(accountType)}>
      {hasPendingTopups ? (<Text TextThin-14 Secondary>
          We are verifying your first deposit. It should be available in the next few hours.{' '}
          <Text Text-14 Primary suppressHighlighting onPress={handleMoreInfo}>
            More info
          </Text>
          .
        </Text>) : (<>
          <Text TextThin-14 Secondary>
            {strings.desc(accountType)}
          </Text>
          {!!isActiveStep && (<>
              <Spacer h={12}/>
              <View style={styles.row}>
                <Button brandLight hug extra square title="Transfer funds" onPress={handleAddFunds}/>
              </View>
            </>)}
        </>)}
    </ProgressCard>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
    },
});
export default FundSecclAccountProgressCard;
